import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FactoryIcon from "@mui/icons-material/Factory";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LayersIcon from "@mui/icons-material/Layers";
import LinkIcon from "@mui/icons-material/Link";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

import { useProjectApi } from "../../../hooks/useProjectApi";
import { BreadcrumbsTable } from "../../../components/common/HelperFunc";
import { AutoPilotGuide } from "../../../components/common/AutoPilotGuide";

const EMPTY_FORM = {
  location_name: "",
  location_description: "",
  parent_id: "",
};

const EMPTY_ARRAY = [];

const PARENT_TITLE = "정보관리";
const CHILD_TITLE = "라인 관리";

const pageContainerSx = {
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
  p: { xs: "16px", md: "16px 32px 16px 32px" },
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  bgcolor: "var(--mui-palette-background-paper)",
};

const standardPanelSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "0.375rem",
  bgcolor: "#fff",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
};

const iconActionButtonSx = {
  width: 40,
  height: 40,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "0.375rem",
  bgcolor: "#fff",
  "&:hover": {
    bgcolor: "primary.lightOpacity",
    borderColor: "primary.main",
  },
};

const LOCATION_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="location-summary"]',
    title: "장소 현황 확인",
    description: "전체 노드, Root, 최대 깊이, 말단 노드 수를 먼저 확인합니다.",
  },
  {
    target: '[data-autopilot="location-tree"]',
    title: "장소 트리 관리",
    description:
      "장소를 선택하거나 하위 노드를 추가하고, 펼침과 접힘으로 계층을 확인합니다.",
  },
  {
    target: '[data-autopilot="location-detail"]',
    title: "선택 노드 상세",
    description:
      "선택한 장소의 경로, 설명, 하위 노드 수와 연결된 장비 수를 확인합니다.",
  },
  {
    target: '[data-autopilot="location-machines"]',
    title: "장비 배정",
    description:
      "선택한 장소에 장비를 체크한 뒤 저장해서 장소와 장비를 연결합니다.",
  },
];

const getNodeIcon = (depth) => {
  if (depth === 0) return <BusinessIcon />;
  if (depth === 1) return <FactoryIcon />;
  if (depth === 2) return <ApartmentIcon />;
  return <Inventory2Icon />;
};

const getDescendantIds = (node) => {
  const ids = new Set();
  const visit = (current) => {
    (current.children || []).forEach((child) => {
      ids.add(child.id);
      visit(child);
    });
  };
  if (node) visit(node);
  return ids;
};

const flattenTree = (tree = []) => {
  const flat = [];
  const visit = (node) => {
    flat.push(node);
    (node.children || []).forEach(visit);
  };
  tree.forEach(visit);
  return flat;
};

const getMachineDisplayName = (machine) =>
  machine.safe_machine_name ||
  machine.machine_name ||
  machine.machine_manage_code ||
  `설비 ${machine.machine_id}`;

const getMachineSubText = (machine) =>
  [
    machine.machine_manage_code
      ? `관리번호 ${machine.machine_manage_code}`
      : "",
    machine.machine_model_name ? `모델 ${machine.machine_model_name}` : "",
    machine.machine_manufacturer || "",
  ]
    .filter(Boolean)
    .join(" · ");

function SummaryCard({ icon, label, value, color = "#2563eb", helper }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        height: "100%",
        boxShadow: "none",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ color, bgcolor: `${color}1a` }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function LocationTreeNode({
  node,
  selectedId,
  expandedIds,
  search,
  onSelect,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
  readOnly = false,
}) {
  const hasChildren = Boolean(node.children?.length);
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;
  const keyword = search.trim().toLowerCase();
  const isMatched =
    !keyword ||
    node.location_name.toLowerCase().includes(keyword) ||
    String(node.location_description || "")
      .toLowerCase()
      .includes(keyword) ||
    node.path.toLowerCase().includes(keyword);

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          mb: 0.75,
          ml: `${node.depth * 18}px`,
          border: "1px solid",
          borderColor: isSelected ? "primary.main" : "divider",
          borderRadius: "0.375rem",
          bgcolor: isSelected
            ? "primary.lightOpacity"
            : isMatched
            ? "#fff"
            : "#f8fafc",
          opacity: isMatched ? 1 : 0.48,
        }}
      >
        <ListItemButton
          selected={isSelected}
          onClick={() => onSelect(node.id)}
          sx={{ borderRadius: "0.375rem", py: 1 }}
        >
          <IconButton
            size="small"
            disabled={!hasChildren}
            onClick={(event) => {
              event.stopPropagation();
              onToggle(node.id);
            }}
            sx={{ mr: 0.75 }}
          >
            {hasChildren ? (
              isExpanded ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )
            ) : (
              <DeviceHubIcon fontSize="small" />
            )}
          </IconButton>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              mr: 1.25,
              color: "primary.main",
              bgcolor: "primary.lightOpacity",
            }}
          >
            {getNodeIcon(node.depth)}
          </Avatar>
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontWeight: 900 }} noWrap>
                  {node.location_name}
                </Typography>
                <Chip size="small" label={`${node.children_count || 0}`} />
                <Chip
                  size="small"
                  icon={<PrecisionManufacturingIcon />}
                  label={`${node.machine_count || 0}`}
                  variant={node.machine_count ? "filled" : "outlined"}
                  color={node.machine_count ? "primary" : "default"}
                />
              </Stack>
            }
            secondary={node.location_description || "설명 없음"}
            primaryTypographyProps={{ component: "div" }}
          />
          {!readOnly ? (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="하위 장소 추가">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddChild(node);
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="수정">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(node);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="삭제">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(node);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null}
        </ListItemButton>
      </Paper>

      {hasChildren && isExpanded
        ? node.children.map((child) => (
            <LocationTreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              expandedIds={expandedIds}
              search={search}
              onSelect={onSelect}
              onToggle={onToggle}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              readOnly={readOnly}
            />
          ))
        : null}
    </Box>
  );
}

function LocationNodeDialog({
  open,
  mode,
  form,
  parentOptions,
  isSaving,
  onClose,
  onChange,
  onSubmit,
}) {
  const isEdit = mode === "edit";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {isEdit ? "장소 노드 수정" : "장소 노드 추가"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              원하는 깊이만큼 하위 장소를 계속 연결할 수 있습니다.
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f7f8fb" }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            required
            label="장소명"
            value={form.location_name}
            onChange={(event) => onChange("location_name", event.target.value)}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="장소 설명"
            value={form.location_description}
            onChange={(event) =>
              onChange("location_description", event.target.value)
            }
          />
          <FormControl fullWidth>
            <InputLabel>상위 장소</InputLabel>
            <Select
              label="상위 장소"
              value={form.parent_id}
              onChange={(event) => onChange("parent_id", event.target.value)}
            >
              <MenuItem value="">Root</MenuItem>
              {parentOptions.map((node) => (
                <MenuItem key={node.id} value={String(node.id)}>
                  {"　".repeat(node.depth)}
                  {node.location_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="inherit" onClick={onClose}>
          취소
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={isSaving}>
          {isEdit ? "수정 완료" : "추가 완료"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EquipmentAssignment({
  selectedNode,
  machines,
  draftMachineIds,
  machineSearch,
  assignedCount,
  isLoading,
  isSaving,
  hasChanges,
  onMachineSearchChange,
  onToggleMachine,
  onReset,
  onSave,
  readOnly = false,
}) {
  const selectedSet = draftMachineIds;
  const keyword = machineSearch.trim().toLowerCase();
  const filteredMachines = useMemo(() => {
    const filtered = machines.filter((machine) => {
      if (!keyword) return true;
      return [
        machine.machine_id,
        machine.machine_name,
        machine.safe_machine_name,
        machine.machine_manage_code,
        machine.machine_manufacturer,
        machine.machine_model_name,
        machine.machine_standard,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(keyword);
    });

    return filtered.sort((a, b) => {
      const aSelected = selectedSet.has(a.machine_id) ? 1 : 0;
      const bSelected = selectedSet.has(b.machine_id) ? 1 : 0;
      if (aSelected !== bSelected) return bSelected - aSelected;
      return getMachineDisplayName(a).localeCompare(
        getMachineDisplayName(b),
        "ko"
      );
    });
  }, [keyword, machines, selectedSet]);

  return (
    <Paper
      data-autopilot="location-machines"
      elevation={0}
      sx={{
        ...standardPanelSx,
        p: 2,
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: { xs: 460, lg: 0 },
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            sx={{
              width: 36,
              height: 36,
              color: "primary.main",
              bgcolor: "primary.lightOpacity",
            }}
          >
            <PrecisionManufacturingIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900 }}>장비 배정</Typography>
            {selectedNode ? (
              <Typography variant="body2" color="text.secondary" noWrap>
                {selectedNode.path}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Chip
          icon={<LinkIcon />}
          color={assignedCount ? "primary" : "default"}
          label={`${assignedCount}대 배정`}
          sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
        />
      </Stack>

      {!selectedNode ? (
        <Box sx={{ flex: 1, minHeight: 160 }} />
      ) : (
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
          <TextField
            size="small"
            placeholder="설비명, 관리번호, 모델명 검색"
            value={machineSearch}
            onChange={(event) => onMachineSearchChange(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {isLoading ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: 220 }}
            >
              <CircularProgress />
            </Stack>
          ) : machines.length ? (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}>
              <Stack spacing={1}>
                {filteredMachines.map((machine) => {
                  const checked = selectedSet.has(machine.machine_id);
                  return (
                    <Paper
                      key={machine.machine_id}
                      variant="outlined"
                      sx={{
                        borderColor: checked ? "primary.main" : "divider",
                        borderRadius: "0.375rem",
                        bgcolor: checked ? "primary.lightOpacity" : "#fff",
                      }}
                    >
                      <ListItemButton
                        onClick={() => {
                          if (!readOnly) onToggleMachine(machine.machine_id);
                        }}
                        sx={{ py: 1.1, px: 1.25 }}
                      >
                        <Checkbox
                          edge="start"
                          checked={checked}
                          tabIndex={-1}
                          disableRipple
                          disabled={readOnly}
                        />
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 900 }}
                                noWrap
                              >
                                {getMachineDisplayName(machine)}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`ID ${machine.machine_id}`}
                              />
                            </Stack>
                          }
                          secondary={
                            getMachineSubText(machine) || "설비 상세 정보 없음"
                          }
                          primaryTypographyProps={{ component: "div" }}
                        />
                      </ListItemButton>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          ) : (
            <Alert severity="warning">
              등록된 장비가 없습니다. machine_master 데이터를 먼저 확인해주세요.
            </Alert>
          )}

          {!isLoading &&
          machines.length > 0 &&
          filteredMachines.length === 0 ? (
            <Alert severity="info">검색 조건에 맞는 장비가 없습니다.</Alert>
          ) : null}

          {!readOnly ? (
            <>
              <Divider />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<RestartAltIcon />}
                  disabled={!hasChanges || isSaving}
                  onClick={onReset}
                >
                  되돌리기
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!hasChanges || isSaving}
                  onClick={onSave}
                >
                  배정 저장
                </Button>
              </Stack>
            </>
          ) : null}
        </Stack>
      )}
    </Paper>
  );
}

export function StandardLocationManagement() {
  const { group_code, project_code } = useParams();
  const navigate = useNavigate();
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const isReadOnly = useMediaQuery("(max-width:899.95px)");

  const [selectedId, setSelectedId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [machineSearch, setMachineSearch] = useState("");
  const [draftMachineIds, setDraftMachineIds] = useState(new Set());
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);

  const locationQuery = useQuery({
    queryKey: ["risk-location-tree", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/location/tree");
      return response.data || { tree: [], nodes: [], links: [] };
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { tree: [], nodes: [], links: [] },
  });

  const machinesQuery = useQuery({
    queryKey: ["risk-location-machines", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/location/machines");
      return response.data?.machines || [];
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: EMPTY_ARRAY,
  });

  const tree = locationQuery.data?.tree || [];
  const nodes = locationQuery.data?.nodes || flattenTree(tree);
  const selectedNode = nodes.find((node) => node.id === selectedId) || null;

  const locationMachinesQuery = useQuery({
    queryKey: [
      "risk-location-node-machines",
      group_code,
      project_code,
      selectedNode?.id,
    ],
    queryFn: async () => {
      const response = await api.get(
        `/location/nodes/${selectedNode.id}/machines`
      );
      return response.data?.machines || [];
    },
    enabled: Boolean(group_code && project_code && selectedNode?.id),
    placeholderData: EMPTY_ARRAY,
  });

  const machines = machinesQuery.data || EMPTY_ARRAY;
  const assignedMachines = locationMachinesQuery.data || EMPTY_ARRAY;
  const assignedMachineIds = useMemo(
    () =>
      new Set(assignedMachines.map((machine) => Number(machine.machine_id))),
    [assignedMachines]
  );

  useEffect(() => {
    setDraftMachineIds(new Set(assignedMachineIds));
    setMachineSearch("");
  }, [assignedMachineIds, selectedNode?.id]);

  const allNodeIds = useMemo(() => nodes.map((node) => node.id), [nodes]);
  const parentOptions = useMemo(() => {
    if (!editingId) return nodes;
    const editingNode = nodes.find((node) => node.id === editingId);
    const descendantIds = getDescendantIds(editingNode);
    return nodes.filter(
      (node) => node.id !== editingId && !descendantIds.has(node.id)
    );
  }, [editingId, nodes]);

  const summary = useMemo(() => {
    const rootCount = nodes.filter((node) => node.parent_id === null).length;
    const maxDepth = nodes.reduce(
      (max, node) => Math.max(max, Number(node.depth || 0)),
      0
    );
    const leafCount = nodes.filter((node) => !node.children_count).length;
    return {
      total: nodes.length,
      rootCount,
      maxDepth: nodes.length ? maxDepth + 1 : 0,
      leafCount,
    };
  }, [nodes]);

  const hasMachineChanges = useMemo(() => {
    if (draftMachineIds.size !== assignedMachineIds.size) return true;
    for (const machineId of draftMachineIds) {
      if (!assignedMachineIds.has(machineId)) return true;
    }
    return false;
  }, [assignedMachineIds, draftMachineIds]);

  const invalidateTree = () => {
    queryClient.invalidateQueries({
      queryKey: ["risk-location-tree", group_code, project_code],
    });
  };

  const invalidateLocationMachines = () => {
    queryClient.invalidateQueries({
      queryKey: [
        "risk-location-node-machines",
        group_code,
        project_code,
        selectedNode?.id,
      ],
    });
    invalidateTree();
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        location_name: form.location_name,
        location_description: form.location_description,
        parent_id: form.parent_id || null,
      };
      if (dialogMode === "edit") {
        return api.put(`/location/nodes/${editingId}`, payload);
      }
      return api.post("/location/nodes", payload);
    },
    onSuccess: () => {
      toast.success(
        dialogMode === "edit"
          ? "장소 노드가 수정되었습니다."
          : "장소 노드가 추가되었습니다."
      );
      closeDialog();
      invalidateTree();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "장소 노드 저장 중 오류가 발생했습니다."
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) =>
      api.delete("/location/nodes", { data: { ids: [id] } }),
    onSuccess: () => {
      toast.success("장소 노드가 삭제되었습니다.");
      setSelectedId(null);
      invalidateTree();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "장소 노드 삭제 중 오류가 발생했습니다."
      );
    },
  });

  const machineMatchMutation = useMutation({
    mutationFn: async () =>
      api.put(`/location/nodes/${selectedNode.id}/machines`, {
        machine_ids: Array.from(draftMachineIds),
      }),
    onSuccess: () => {
      toast.success("장비 배정이 저장되었습니다.");
      invalidateLocationMachines();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "장비 배정 저장 중 오류가 발생했습니다."
      );
    },
  });

  const toggleMachine = (machineId) => {
    setDraftMachineIds((prev) => {
      const next = new Set(prev);
      if (next.has(machineId)) next.delete(machineId);
      else next.add(machineId);
      return next;
    });
  };

  const resetMachineDraft = () => {
    setDraftMachineIds(new Set(assignedMachineIds));
  };

  const saveMachineMatches = () => {
    if (!selectedNode) {
      toast.warning("장비를 배정할 장소 노드를 선택해주세요.");
      return;
    }
    machineMatchMutation.mutate();
  };

  const openCreateDialog = (parentId = "") => {
    setDialogMode("create");
    setEditingId(null);
    setForm({ ...EMPTY_FORM, parent_id: parentId ? String(parentId) : "" });
    setDialogOpen(true);
  };

  const openEditDialog = (node) => {
    setDialogMode("edit");
    setEditingId(node.id);
    setForm({
      location_name: node.location_name,
      location_description: node.location_description || "",
      parent_id: node.parent_id ? String(node.parent_id) : "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogMode("create");
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!String(form.location_name || "").trim()) {
      toast.warning("장소명을 입력해주세요.");
      return;
    }
    saveMutation.mutate();
  };

  const handleDelete = (node) => {
    if (node.children_count > 0) {
      toast.warning("하위 장소가 있는 노드는 삭제할 수 없습니다.");
      return;
    }
    if (window.confirm(`"${node.location_name}" 노드를 삭제할까요?`)) {
      deleteMutation.mutate(node.id);
    }
  };

  const toggleExpanded = (nodeId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const expandAll = () => setExpandedIds(new Set(allNodeIds));
  const collapseAll = () => setExpandedIds(new Set());

  const startLocationAssessment = () => {
    if (!selectedNode) return;
    const locationIds = [
      selectedNode.id,
      ...Array.from(getDescendantIds(selectedNode)),
    ];
    const params = new URLSearchParams({
      location_id: String(selectedNode.id),
      location_ids: locationIds.join(","),
      location_name: selectedNode.location_name || "",
    });
    navigate(`/safe/${group_code}/${project_code}/assessment/wizard?${params}`);
  };

  return (
    <Box
      sx={{
        bgcolor: "var(--mui-palette-background-paper)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      <Box sx={pageContainerSx}>
        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          <Box
            data-autopilot="location-summary"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr)) auto",
              },
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            <Box>
              <SummaryCard
                icon={<AccountTreeIcon />}
                label="전체 노드"
                value={`${summary.total}개`}
              />
            </Box>
            <Box>
              <SummaryCard
                icon={<BusinessIcon />}
                label="Root"
                value={`${summary.rootCount}개`}
                color="#0f766e"
              />
            </Box>
            <Box>
              <SummaryCard
                icon={<LayersIcon />}
                label="최대 깊이"
                value={`${summary.maxDepth}단계`}
                color="#7c3aed"
              />
            </Box>
            <Box>
              <SummaryCard
                icon={<DeviceHubIcon />}
                label="말단 노드"
                value={`${summary.leafCount}개`}
                color="#ea580c"
              />
            </Box>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
              sx={{
                minWidth: { md: 172 },
                alignSelf: "center",
                gridColumn: { xs: "1 / -1", md: "auto" },
              }}
            >
              <Tooltip title="오토파일럿">
                <IconButton
                  color="primary"
                  onClick={() => setAutopilotOpen(true)}
                  sx={iconActionButtonSx}
                >
                  <PlayCircleOutlineIcon />
                </IconButton>
              </Tooltip>
              {!isReadOnly ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openCreateDialog()}
                >
                  Root 추가
                </Button>
              ) : null}
            </Stack>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            ...standardPanelSx,
            p: 1.5,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Grid container spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
            <Grid item xs={12} lg={7} sx={{ display: "flex" }}>
              <Paper
                data-autopilot="location-tree"
                elevation={0}
                sx={{
                  ...standardPanelSx,
                  p: 2,
                  boxShadow: "none",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  height: { xs: "auto", lg: "100%" },
                  minHeight: { xs: 560, lg: 0 },
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  sx={{ mb: 2 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DeviceHubIcon color="primary" />
                    <Typography sx={{ fontWeight: 900 }}>장소 트리</Typography>
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      size="small"
                      placeholder="장소명, 설명, 경로 검색"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button size="small" onClick={expandAll}>
                      모두 펼침
                    </Button>
                    <Button size="small" color="inherit" onClick={collapseAll}>
                      접기
                    </Button>
                  </Stack>
                </Stack>

                {locationQuery.isLoading ? (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{ minHeight: 380 }}
                  >
                    <CircularProgress />
                  </Stack>
                ) : tree.length ? (
                  <Box
                    sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}
                  >
                    {tree.map((node) => (
                      <LocationTreeNode
                        key={node.id}
                        node={node}
                        selectedId={selectedId}
                        expandedIds={expandedIds}
                        search={search}
                        onSelect={setSelectedId}
                        onToggle={toggleExpanded}
                        onAddChild={(target) => openCreateDialog(target.id)}
                        onEdit={openEditDialog}
                        onDelete={handleDelete}
                        readOnly={isReadOnly}
                      />
                    ))}
                  </Box>
                ) : (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={2}
                    sx={{ minHeight: 380 }}
                  >
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: "primary.lightOpacity",
                        color: "primary.main",
                      }}
                    >
                      <AccountTreeIcon />
                    </Avatar>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography sx={{ fontWeight: 900 }}>
                        아직 장소 노드가 없습니다.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Root 노드를 추가해서 장소 계층을 시작하세요.
                      </Typography>
                    </Box>
                    {!isReadOnly ? (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => openCreateDialog()}
                      >
                        Root 추가
                      </Button>
                    ) : null}
                  </Stack>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5} sx={{ display: "flex" }}>
              <Stack
                spacing={1.5}
                sx={{
                  width: "100%",
                  height: { xs: "auto", lg: "100%" },
                  minHeight: { xs: 0, lg: 0 },
                }}
              >
                <Paper
                  data-autopilot="location-detail"
                  elevation={0}
                  sx={{
                    ...standardPanelSx,
                    p: 2,
                    boxShadow: "none",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <CheckCircleIcon
                      color={selectedNode ? "primary" : "disabled"}
                    />
                    <Typography sx={{ fontWeight: 900 }}>
                      선택 노드 상세
                    </Typography>
                  </Stack>
                  {selectedNode ? (
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          장소명
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>
                          {selectedNode.location_name}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          전체 경로
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {selectedNode.path}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          설명
                        </Typography>
                        <Typography variant="body2">
                          {selectedNode.location_description || "설명 없음"}
                        </Typography>
                      </Box>
                      <Divider />
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Chip
                          size="small"
                          label={`깊이 ${selectedNode.depth + 1}`}
                        />
                        <Chip
                          size="small"
                          label={`하위 ${selectedNode.children_count || 0}개`}
                        />
                        <Chip
                          size="small"
                          color={
                            selectedNode.machine_count ? "primary" : "default"
                          }
                          icon={<PrecisionManufacturingIcon />}
                          label={`장비 ${selectedNode.machine_count || 0}대`}
                        />
                        <Chip size="small" color="success" label="활성" />
                      </Stack>
                      {!isReadOnly ? (
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<AssessmentIcon />}
                            disabled={!selectedNode.machine_count}
                            onClick={startLocationAssessment}
                          >
                            라인 평가
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => openCreateDialog(selectedNode.id)}
                          >
                            하위 추가
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => openEditDialog(selectedNode)}
                          >
                            수정
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDelete(selectedNode)}
                          >
                            삭제
                          </Button>
                        </Stack>
                      ) : null}
                    </Stack>
                  ) : null}
                </Paper>

                <EquipmentAssignment
                  selectedNode={selectedNode}
                  machines={machines}
                  draftMachineIds={draftMachineIds}
                  machineSearch={machineSearch}
                  assignedCount={draftMachineIds.size}
                  isLoading={
                    machinesQuery.isLoading || locationMachinesQuery.isLoading
                  }
                  isSaving={machineMatchMutation.isPending}
                  hasChanges={hasMachineChanges}
                  onMachineSearchChange={setMachineSearch}
                  onToggleMachine={toggleMachine}
                  onReset={resetMachineDraft}
                  onSave={saveMachineMatches}
                  readOnly={isReadOnly}
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Alert severity="info">
          왼쪽 장소 트리에서 노드를 선택하면 상세 정보와 장비 배정을 함께 관리할
          수 있습니다.
        </Alert>

        <LocationNodeDialog
          open={dialogOpen}
          mode={dialogMode}
          form={form}
          parentOptions={parentOptions}
          isSaving={saveMutation.isPending}
          onClose={closeDialog}
          onChange={handleFormChange}
          onSubmit={handleSubmit}
        />
        <AutoPilotGuide
          open={autopilotOpen}
          steps={LOCATION_AUTOPILOT_STEPS}
          stepIndex={autopilotStepIndex}
          onStepChange={setAutopilotStepIndex}
          onClose={() => setAutopilotOpen(false)}
        />
      </Box>
    </Box>
  );
}
